// @flow

import { get, isEmpty } from 'lodash';
import moment from 'moment';
import URI from 'urijs';
import { messageFormat as message } from '@clubmed/utils';

import { buildUrl } from 'Routes/routeBuilder';
import { type IconName } from 'Components/frameworks/icon/Icon.jsx';
import { ICONS } from 'Components/utils/icon/icons';
import { BOOKING_PAYMENT_STATUS_OPTION } from 'Utils/booking-payment-status/bookingPaymentStatus.constants';
import { BOOKING_STATUS_ARCHIVED } from 'Utils/booking-status/bookingStatus.constants';
import { momentFromApiDate } from 'Utils/date/apiDate';

type CMSContentType = {
  pleaseContactClubMedLabel?: string,
  pleaseContactExternalLabel?: string,
  travelAdvisor?: {
    phoneNumber: string,
  },
};

function formatPhoneNumber(agencyPhoneNumber: ?string): ?string {
  return agencyPhoneNumber && `tel:${agencyPhoneNumber.replace(/\s/g, '')}`;
}

function getPhoneNumber(vendor: BookingVendor, fallbackPhoneNumber: ?string): ?string {
  return (
    vendor &&
    (vendor.isSoldByClubMed && !vendor.phoneNumber ? fallbackPhoneNumber : vendor.phoneNumber)
  );
}

function getAgencyLabel(vendor: BookingVendor, cmsContent: ?CMSContentType): ?string {
  const pleaseContactClubMedLabel = get(cmsContent, 'pleaseContactClubMedLabel', null);
  const pleaseContactExternalLabel = get(cmsContent, 'pleaseContactExternalLabel', null);

  return (
    vendor &&
    (vendor.isSoldByClubMed
      ? pleaseContactClubMedLabel
      : pleaseContactExternalLabel &&
        message(pleaseContactExternalLabel).format({
          agencyName: vendor.name,
        }))
  );
}

type getAgencyDetailsType = {
  vendor: BookingVendor,
  travelAdvisor: CMSTravelAdvisor,
  dedicatedJoyviewHotlineNumber: ?string,
  isJoyview: ?boolean,
};

function getAgencyDetails({
  vendor,
  travelAdvisor,
  dedicatedJoyviewHotlineNumber,
  isJoyview,
}: getAgencyDetailsType): {} {
  const agencyPhoneNumber =
    isJoyview && !isEmpty(dedicatedJoyviewHotlineNumber)
      ? dedicatedJoyviewHotlineNumber
      : this.getPhoneNumber(vendor, get(travelAdvisor, 'phoneNumber'));

  return {
    label: agencyPhoneNumber,
    url: this.formatPhoneNumber(agencyPhoneNumber),
  };
}

function isPastTrip(returnDate: string): boolean {
  if (!returnDate) {
    return false;
  }
  return moment(Date.now()).isAfter(moment(returnDate, 'YYYYMMDD'));
}

function shouldDisplayPriceDetail(isSoldByClubMed: boolean, displayPriceDetail: boolean): boolean {
  return isSoldByClubMed || displayPriceDetail;
}

function getPackageIcon(isAllInclusive: boolean, isJoyview: boolean): IconName {
  const iconAccommodations = isJoyview ? ICONS.JOYVIEW_HAND : ICONS.ACCOMODATIONS;
  return isAllInclusive ? ICONS.ALL_INCLUSIVE : iconAccommodations;
}

function isOption(informationStatus?: BookingStatus) {
  return informationStatus === BOOKING_PAYMENT_STATUS_OPTION;
}

function isArchived(bookingStatus: BookingStatus) {
  return bookingStatus === BOOKING_STATUS_ARCHIVED;
}

function shouldDisplayLinkList(
  userBooking: Booking,
  currentUri: URI = new URI('#'),
  isFeatureFlippedOff: boolean = false,
  stay: *,
): boolean {
  const prepareMyStayDirectory = new URI(
    buildUrl('prepareMyStay', { bookingId: get(userBooking, 'information.bookingId') }),
  ).directory();
  const isOnPrepareMyStay = currentUri.directory() === prepareMyStayDirectory.toString();

  return (
    (!isOption(get(userBooking, 'information.status')) &&
      !isPastTrip(get(stay, 'returnDate')) &&
      !isFeatureFlippedOff) ||
    isOnPrepareMyStay
  );
}

function hasTransport(stay: Stay): boolean {
  return !!(get(stay, 'departureCity') || get(stay, 'arrivalCity'));
}

function hasTransfer(transfers?: BookingTransferDetails): boolean {
  return !!(transfers && (!isEmpty(transfers.inbound) || !isEmpty(transfers.outbound)));
}

function findStay(booking: Booking | PrepareMyStayBooking | TripSummaryBooking, stayId: string) {
  const stays = get(booking, 'information.stays', []);
  if (!stayId && stays.length === 1) {
    return stays[0];
  }

  return stays.find(s => s.id === stayId);
}

function isFirstStay(booking: Booking | PrepareMyStayBooking, stayId: string) {
  return get(booking, 'information.stays.0', {}).id === stayId;
}

function findStayFormality(stayFormalities: *, stayId: string) {
  return (stayFormalities || []).find(s => s.stayId === stayId);
}

function findTransferDetails(booking: Booking | PrepareMyStayBooking, stayId: string) {
  const stay = findStay(booking, stayId);
  if (!stay) return;
  return (get(booking, 'transferDetails') || []).find(
    transfer =>
      get(transfer, 'inbound.date') === (get(stay, 'departureDate') || Number.NaN).toString() ||
      get(transfer, 'outbound.date') === (get(stay, 'returnDate') || Number.NaN).toString(),
  );
}

function isMultiStay(booking: Booking) {
  const stays = get(booking, 'information.stays');
  return stays && stays.length > 1;
}

function findCurrentStay(booking: Booking) {
  const stays = get(booking, 'information.stays', []);
  if (stays.length === 0) return null;
  if (stays.length === 1) return stays[0];

  const now = moment();
  const res = stays.find(
    stay =>
      stay.departureDate &&
      stay.returnDate &&
      momentFromApiDate(stay.departureDate).isBefore(now) &&
      momentFromApiDate(stay.returnDate).isAfter(now),
  );

  return res || stays[0];
}

function isTour(stay: { productType?: string }) {
  return stay.productType === 'TOUR';
}

function isCruise(stay: { productType?: string }) {
  return stay.productType === 'CRUISE';
}

function getTranportIcon(transportType?: string) {
  switch (transportType) {
    case 'TRAIN':
      return ICONS.TRAIN;
    case 'BOAT':
    case 'FERRY':
      return ICONS.BOAT;
    default:
      return ICONS.TRANSPORT;
  }
}

function isSoldByClubmed(vendor?: string) {
  return vendor === 'CLUBMED';
}

export default {
  formatPhoneNumber,
  getPhoneNumber,
  getAgencyLabel,
  getAgencyDetails,
  getPackageIcon,
  isPastTrip,
  shouldDisplayPriceDetail,
  shouldDisplayLinkList,
  hasTransport,
  hasTransfer,
  isOption,
  isArchived,
  findStay,
  isMultiStay,
  findStayFormality,
  findCurrentStay,
  isFirstStay,
  findTransferDetails,
  isTour,
  isCruise,
  getTranportIcon,
  isSoldByClubmed,
};
